// ============================================================
// 📁 آدرس این فایل در پروژه (فایل جدید):
//    src/pages/finance/journal/TxnReportModal.tsx
//
// پنجره‌ی گزارش هر تراکنش: انتخاب بازه (همان تاریخ‌ساز) + خروجی PDF و Excel حرفه‌ای
// گزارش از «همه‌ی رکوردها» یا «فقط مطابق فیلترهای همان صفحه» گرفته می‌شود (نه فقط صفحه‌ی جاری)
// ============================================================

import { useState } from "react";
import { FileText, FileSpreadsheet, Loader2 } from "lucide-react";
import PersianDatePicker from "../../../utils/PersianDatePicker";
import { journalAPI } from "../../../services/api";
import { buildSpec, exportSpecExcel, printSpecPdf, jal } from "../../../utils/txnReport";
import { Modal, Lbl } from "./Purchase";
import SearchableSelect from "../../../components/SearchableSelect";
import { JournalFilterValues } from "./JournalFilter";
import { filterToParams } from "./useTxnList";

interface Props {
  type: "purchases" | "sales" | "payments_in" | "payments_out" | "expenses" | "cogs" | "transfers";
  title: string;
  filter: JournalFilterValues;
  filtersLabel: string;       // توضیح متنی فیلترهای فعال صفحه
  extraParams?: Record<string, any>; // مثلاً sale_type
  itemOptions?: { value: string; label: string; type?: string; id?: any; name?: string }[]; // اول جنس انتخاب می‌شود، بعد دسته‌بندی‌ها (COGS)
  categoryGroups?: { title: string; items: { id: string; name: string }[] }[]; // انتخاب دسته‌بندی (مصارف و COGS)
  onClose: () => void;
  T: any;
  dark: boolean;
}

export default function TxnReportModal({ type, title, filter, filtersLabel, extraParams, categoryGroups, itemOptions, onClose, T, dark }: Props) {
  const hasFilters = !!(filter.search || filter.counterparty || filter.productName || filter.account || filter.category || filter.currency || filter.amountMin || filter.amountMax);
  const [mode, setMode] = useState<"all" | "range">(filter.dateFrom || filter.dateTo ? "range" : "all");
  const [from, setFrom] = useState(filter.dateFrom || "");
  const [to, setTo] = useState(filter.dateTo || "");
  const [useFilters, setUseFilters] = useState(hasFilters);
  const [withPending, setWithPending] = useState(false);
  const [busy, setBusy] = useState<"" | "pdf" | "xls">("");
  const [err, setErr] = useState("");
  const [itemVal, setItemVal] = useState<string | null>(null); // null = هنوز انتخاب نشده
  const [cats, setCats] = useState<string[]>(Array.isArray(extraParams?.categories) ? extraParams!.categories : []);

  const period = mode === "all" || (!from && !to) ? "همه‌ی دوره‌ها" : `${from ? jal(from) : "ابتدا"} تا ${to ? jal(to) : "امروز"}`;

  const run = async (kind: "pdf" | "xls") => {
    setBusy(kind); setErr("");
    try {
      const base = useFilters ? filterToParams({ ...filter, dateFrom: "", dateTo: "" }) : {};
      const params: Record<string, any> = { ...base, ...(extraParams || {}), type };
      if (itemOptions && itemVal) { const it = itemOptions.find(o => o.value === itemVal); if (it) { params.product_item_id = it.id; params.product_item_type = it.type; } }
      if (categoryGroups) { params.categories = cats.length ? cats : undefined; if (cats.length) delete params.category; }
      if (mode === "range") { if (from) params.date_from = from; if (to) params.date_to = to; }
      if (!withPending) params.final_only = 1;
      Object.keys(params).forEach(k => params[k] === undefined && delete params[k]);
      const res = await journalAPI.reportData(params);
      if (!res || !res.success) { setErr(res?.message || "خطا در دریافت اطلاعات گزارش"); setBusy(""); return; }
      const spec = buildSpec(type, res.rows || [], { fx: res.fx_summary });
      const itemName = itemOptions && itemVal ? (itemOptions.find(o => o.value === itemVal)?.name || "") : "";
      const catNames = categoryGroups ? categoryGroups.flatMap(g => g.items).filter(i => cats.includes(i.id)).map(i => i.name) : [];
      const parts = [useFilters ? filtersLabel : "", itemName ? `جنس: ${itemName}` : "", catNames.length ? `دسته‌بندی: ${catNames.join("، ")}` : "", withPending ? "" : "فقط تراکنش‌های نهایی"].filter(Boolean);
      const meta = { periodLabel: period, filtersLabel: parts.join(" | ") };
      if (kind === "xls") await exportSpecExcel(spec, meta);
      else if (!printSpecPdf(spec, meta)) setErr("مرورگر پنجره‌ی چاپ را مسدود کرد؛ اجازه‌ی باز شدن پنجره را بدهید.");
    } catch (e: any) {
      setErr("خطا در تولید گزارش");
    }
    setBusy("");
  };

  const choice = (active: boolean, c: string): React.CSSProperties => ({
    flex: 1, padding: "10px 14px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 700, textAlign: "center",
    border: `2px solid ${active ? c : T.cardBorder}`, background: active ? c + "18" : "transparent", color: active ? c : T.subtext,
    fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
  });

  return (
    <Modal title={`گزارش ${title}`} onClose={onClose} dark={dark} T={T} maxWidth={1280}>
      <div style={{ display: "flex", flexDirection: "column", gap: 22, fontFamily: "'Vazirmatn','Segoe UI',sans-serif", minHeight: 680, zoom: 1.2 } as any}>
        <div>
          <Lbl T={T}>بازه‌ی گزارش</Lbl>
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <div onClick={() => setMode("all")} style={choice(mode === "all", "#10b981")}>همه‌ی تاریخ‌ها</div>
            <div onClick={() => setMode("range")} style={choice(mode === "range", "#3b82f6")}>از تاریخ … تا تاریخ …</div>
          </div>
          {mode === "range" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><Lbl T={T}>از تاریخ</Lbl><PersianDatePicker value={from} onChange={setFrom} /></div>
              <div><Lbl T={T}>تا تاریخ</Lbl><PersianDatePicker value={to} onChange={setTo} /></div>
            </div>
          )}
        </div>

        {itemOptions && (
          <div>
            <Lbl T={T}>۱) اول جنس یا خدمت را انتخاب کنید</Lbl>
            <SearchableSelect
              options={[{ value: "", label: "— همه‌ی اجناس —" }, ...itemOptions.map(o => ({ value: o.value, label: o.label }))]}
              value={itemVal ?? ""}
              onChange={(v: string) => setItemVal(v)}
              placeholder="انتخاب جنس"
            />
          </div>
        )}

        {categoryGroups && (!itemOptions || itemVal !== null) && (
          <div>
            <Lbl T={T}>{itemOptions ? "۲) دسته‌بندی‌ها" : "گزارش بر اساس دسته‌بندی"} (اگر چیزی انتخاب نکنید، همه‌ی دسته‌ها)</Lbl>
            {categoryGroups.map(g => (
              <div key={g.title} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, color: T.subtext, margin: "6px 0" }}>{g.title}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {g.items.map(it => {
                    const on = cats.includes(it.id);
                    return <div key={it.id} onClick={() => setCats(c => on ? c.filter(x => x !== it.id) : [...c, it.id])} style={{ padding: "7px 14px", borderRadius: 20, cursor: "pointer", fontSize: 12.5, fontWeight: 700, border: `1.5px solid ${on ? "#8b5cf6" : T.cardBorder}`, background: on ? "rgba(139,92,246,0.15)" : "transparent", color: on ? "#8b5cf6" : T.text }}>{on ? "✓ " : ""}{it.name}</div>;
                  })}
                </div>
              </div>
            ))}
            {cats.length > 0 && <button onClick={() => setCats([])} style={{ marginTop: 4, padding: "5px 14px", borderRadius: 8, border: "1.5px solid #ef4444", background: "transparent", color: "#ef4444", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>× پاک کردن انتخاب</button>}
          </div>
        )}

        {hasFilters && (
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", borderRadius: 10, background: dark ? "rgba(139,92,246,0.10)" : "#f5f3ff", border: `1px solid ${T.cardBorder}`, cursor: "pointer" }}>
            <input type="checkbox" checked={useFilters} onChange={e => setUseFilters(e.target.checked)} style={{ marginTop: 3 }} />
            <span style={{ fontSize: 12.5, color: T.text, lineHeight: 1.8 }}>
              فقط رکوردهای مطابق فیلترهای فعلی صفحه
              <div style={{ fontSize: 11.5, color: T.subtext }}>{filtersLabel}</div>
            </span>
          </label>
        )}

        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 12.5, color: T.text }}>
          <input type="checkbox" checked={withPending} onChange={e => setWithPending(e.target.checked)} />
          رکوردهای «در انتظار تأیید» هم در گزارش باشند
        </label>

        {err && <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(239,68,68,0.1)", color: "#ef4444", fontSize: 12.5, fontWeight: 700 }}>{err}</div>}

        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button disabled={!!busy} onClick={() => run("pdf")} style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 24px", borderRadius: 12, border: "none", cursor: busy ? "wait" : "pointer", background: "linear-gradient(120deg,#ef4444,#dc2626)", color: "#fff", fontWeight: 800, fontSize: 13.5, fontFamily: "inherit", boxShadow: "0 4px 14px rgba(239,68,68,0.3)", opacity: busy ? 0.7 : 1 }}>
            {busy === "pdf" ? <Loader2 size={16} className="spin" /> : <FileText size={16} />} PDF
          </button>
          <button disabled={!!busy} onClick={() => run("xls")} style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 24px", borderRadius: 12, border: "none", cursor: busy ? "wait" : "pointer", background: "linear-gradient(120deg,#10b981,#059669)", color: "#fff", fontWeight: 800, fontSize: 13.5, fontFamily: "inherit", boxShadow: "0 4px 14px rgba(16,185,129,0.3)", opacity: busy ? 0.7 : 1 }}>
            {busy === "xls" ? <Loader2 size={16} className="spin" /> : <FileSpreadsheet size={16} />} Excel
          </button>
        </div>
      </div>
    </Modal>
  );
}
